import { Fragment, useState } from "react";
import { Plus, Trash2, Info, AlertTriangle } from "lucide-react";
import {
  CheckChips,
  Field,
  NumberInput,
  Panel,
  Select,
  Switch,
  TabBar,
  ToggleRow,
} from "./ui";
import {
  CONTINUE_ON_OPTIONS,
  DATAMATCH_FIELDS,
  PRODUCT_SCHEMA,
  SERVICE_LABELS,
  SERVICE_OPTIONS,
  type CheckConfig,
  type ProductId,
} from "./types";

type Props = {
  id: ProductId;
  config: CheckConfig;
  onChange: (next: CheckConfig) => void;
};

const TABS = [
  { id: "provider", label: "Provider & documents" },
  { id: "datamatch", label: "Data match" },
  { id: "validation", label: "Validation" },
  { id: "decision", label: "Decisioning" },
  { id: "advanced", label: "Flags & other" },
];

const CARD_FIRST_DIGIT_OPTIONS = ["0", "4", "6", "8"];
const CARD_LAST_DIGIT_OPTIONS = ["0", "4"];

function cardMaskPreview(first: number, last: number, total = 16): string {
  const mid = Math.max(0, total - first - last);
  const parts = [
    first > 0 ? "N".repeat(first) : "",
    mid > 0 ? "*".repeat(mid) : "",
    last > 0 ? "N".repeat(last) : "",
  ].filter(Boolean);
  return `${parts.join(" ")} · owner name · business / private`;
}

export function ProductConfig({ id, config, onChange }: Props) {
  const [tab, setTab] = useState("provider");
  const schema = PRODUCT_SCHEMA[id];
  // POI/IDV always routes through Incode only — no provider picker.
  const forceIncodeOnly = id === "idv";
  const requiredProcessingProviders =
    id === "poa" ? ["incode", "incode+sonio"] : forceIncodeOnly ? ["incode"] : [];
  const displayedProcessingProviders = (providers: string[]) =>
    requiredProcessingProviders.length > 0
      ? requiredProcessingProviders
      : providers;
  const branchProviders = (providers: string[]) =>
    forceIncodeOnly ? ["incode"] : displayedProcessingProviders(providers);
  const activeMatches = Object.values(config.settings.datamatch).filter((r) => r.active).length;
  const activeValidations = Object.values(config.settings.validation).filter((v) => v.active).length;
  const cardFirstDigits = config.settings.cardFirstDigits ?? 6;
  const cardLastDigits = config.settings.cardLastDigits ?? 4;

  const set = (patch: Partial<CheckConfig>) => onChange({ ...config, ...patch });
  const setSettings = (patch: Partial<CheckConfig["settings"]>) =>
    onChange({ ...config, settings: { ...config.settings, ...patch } });

  return (
    <div className="space-y-6">
      <TabBar
        active={tab}
        onChange={setTab}
        tabs={TABS.map((t) =>
          t.id === "datamatch"
            ? { ...t, badge: activeMatches }
            : t.id === "validation"
              ? { ...t, badge: activeValidations }
              : t,
        )}
      />

      {tab === "provider" && id === "card" && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
            <Field
              label="Provider"
              hint="Credit card scanning runs on Sonio only."
            >
              <Select
                value="sonio"
                onChange={() => set({ provider: "sonio" })}
                options={schema.providers}
              />
            </Field>
            <ToggleRow
              label="Manual check"
              hint="Always route this check to a human analyst"
              on={config.manual}
              onChange={(v) => set({ manual: v })}
            />
          </div>

          <ToggleRow
            label="Credit card verification (with Stripe)"
            hint="Confirm the cardholder and that the card is active via Stripe"
            on={config.settings.cardStripeVerification ?? false}
            onChange={(v) => setSettings({ cardStripeVerification: v })}
          />

          <Panel title="Card number extraction">
            <p className="mb-4 text-[12px] font-medium text-primary">
              Choose how many digits are captured from the scanned card.
              Everything in between stays masked.
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field
                label="First digits"
                hint="Leading digits (BIN) to extract."
              >
                <Select
                  value={String(cardFirstDigits)}
                  onChange={(v) =>
                    setSettings({ cardFirstDigits: Number(v) })
                  }
                  options={CARD_FIRST_DIGIT_OPTIONS}
                />
              </Field>
              <Field label="Last digits" hint="Trailing digits to extract.">
                <Select
                  value={String(cardLastDigits)}
                  onChange={(v) => setSettings({ cardLastDigits: Number(v) })}
                  options={CARD_LAST_DIGIT_OPTIONS}
                />
              </Field>
            </div>
            <div className="mt-4 rounded-lg bg-surface-50 px-3 py-2.5 font-mono text-[12px] text-muted-foreground ring-1 ring-border">
              {cardMaskPreview(cardFirstDigits, cardLastDigits)}
            </div>
          </Panel>
        </div>
      )}

      {tab === "provider" && id !== "card" && (
        <div className="space-y-5">
          <div className="max-w-sm">
            <Field label="Provider" hint="Engine resolving this check.">
              <Select
                value={config.provider}
                onChange={(v) => set({ provider: v })}
                options={schema.providers}
              />
            </Field>
          </div>

          <Panel
            title="Check options"
            description="Each row is one accepted branch: the document types the user may submit, and the providers that process them. Serialised as document:TYPES:providers."
            right={
              <button
                type="button"
                onClick={() =>
                  set({
                    checkoptions: [
                      ...config.checkoptions,
                      {
                        documents: [],
                        providers: forceIncodeOnly
                          ? ["incode"]
                          : [schema.providerRouting[0]],
                      },
                    ],
                  })
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-semibold text-muted-foreground hover:border-primary/40 hover:text-primary"
              >
                <Plus className="size-3.5" />
                Add branch
              </button>
            }
          >
            <div className="space-y-3">
              {config.checkoptions.map((opt, i) => (
                <div key={i} className="rounded-xl border border-border bg-surface-50 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary">
                      Branch {i + 1}
                    </span>
                    {config.checkoptions.length > 1 && (
                      <button
                        type="button"
                        onClick={() =>
                          set({ checkoptions: config.checkoptions.filter((_, j) => j !== i) })
                        }
                        className="rounded p-1 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    )}
                  </div>
                  <div
                    className={`grid grid-cols-1 gap-4 ${
                      forceIncodeOnly ? "" : "md:grid-cols-2"
                    }`}
                  >
                    <Field label="Document types">
                      <CheckChips
                        options={schema.documents}
                        value={opt.documents}
                        onChange={(v) =>
                          set({
                            checkoptions: config.checkoptions.map((o, j) =>
                              j === i
                                ? {
                                    ...o,
                                    documents: v,
                                    providers: forceIncodeOnly
                                      ? ["incode"]
                                      : o.providers,
                                  }
                                : o,
                            ),
                          })
                        }
                      />
                    </Field>
                    {!forceIncodeOnly && (
                      <Field label="Processing providers">
                        <CheckChips
                          options={schema.providerRouting}
                          value={displayedProcessingProviders(opt.providers)}
                          readOnly={requiredProcessingProviders.length > 0}
                          onChange={(v) =>
                            set({
                              checkoptions: config.checkoptions.map((o, j) =>
                                j === i
                                  ? {
                                      ...o,
                                      providers:
                                        displayedProcessingProviders(v),
                                    }
                                  : o,
                              ),
                            })
                          }
                        />
                      </Field>
                    )}
                  </div>
                  <div className="mt-3 rounded-lg bg-white px-3 py-2 font-mono text-[11px] text-muted-foreground ring-1 ring-border">
                    document:{opt.documents.join(",") || "—"}:
                    {branchProviders(opt.providers).join(",") || "—"}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {tab === "datamatch" && (
        <div className="space-y-5">
          {id !== "card" && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ToggleRow
                label="Person data match"
                hint="Compare extracted person data against the case"
                on={config.settings.persondatamatch}
                onChange={(v) => setSettings({ persondatamatch: v })}
              />
              <ToggleRow
                label="Address data match"
                hint="Compare extracted address against the case"
                on={config.settings.addressdatamatch}
                onChange={(v) => setSettings({ addressdatamatch: v })}
              />
            </div>
          )}

          <Panel
            title="Default match limits"
            description="Fallback thresholds used by any field without its own limits. -1 disables the threshold."
          >
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-destructive">Fail below</span>
                <NumberInput
                  value={config.settings.datamatchlimits.fail}
                  onChange={(v) =>
                    setSettings({
                      datamatchlimits: { ...config.settings.datamatchlimits, fail: v },
                    })
                  }
                  suffix="%"
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-primary">Review below</span>
                <NumberInput
                  value={config.settings.datamatchlimits.review}
                  onChange={(v) =>
                    setSettings({
                      datamatchlimits: { ...config.settings.datamatchlimits, review: v },
                    })
                  }
                  suffix="%"
                />
              </div>
            </div>
          </Panel>

          <Panel
            title="Field-level matching"
            description="Enable a field to score it, choose which services provide the reference data, and set its own fail / review thresholds."
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    <th className="pb-2 pl-1">Field</th>
                    <th className="pb-2">Services</th>
                    <th className="pb-2 text-right">Fail &lt;</th>
                    <th className="pb-2 pr-1 text-right">Review &lt;</th>
                  </tr>
                </thead>
                <tbody>
                  {DATAMATCH_FIELDS.map((f, i) => {
                    const rule = config.settings.datamatch[f.key];
                    if (!rule) return null;
                    const prevGroup = DATAMATCH_FIELDS[i - 1]?.group;
                    const update = (patch: Partial<typeof rule>) =>
                      setSettings({
                        datamatch: {
                          ...config.settings.datamatch,
                          [f.key]: { ...rule, ...patch },
                        },
                      });
                    return (
                      <Fragment key={f.key}>
                        {f.group !== prevGroup && (
                          <tr>
                            <td
                              colSpan={4}
                              className="pt-3 pb-1 pl-1 text-[11px] font-bold uppercase tracking-[0.14em] text-primary"
                            >
                              {f.group}
                            </td>
                          </tr>
                        )}
                        <tr
                          className={`border-t border-border align-middle ${
                            rule.active ? "" : "opacity-60"
                          }`}
                        >
                          <td className="py-2 pl-1">
                            <div className="flex items-center gap-3">
                              <Switch on={rule.active} onChange={(v) => update({ active: v })} />
                              <div>
                                <div className="text-sm font-medium">{f.label}</div>
                                <div className="font-mono text-[10px] text-muted-foreground">
                                  {f.key}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-2">
                            <CheckChips
                              options={SERVICE_OPTIONS}
                              value={rule.service}
                              onChange={(v) => update({ service: v })}
                              labels={SERVICE_LABELS}
                            />
                          </td>
                          <td className="py-2 text-right">
                            <div className="flex justify-end">
                              <NumberInput
                                value={rule.limits.fail}
                                onChange={(v) =>
                                  update({ limits: { ...rule.limits, fail: v } })
                                }
                              />
                            </div>
                          </td>
                          <td className="py-2 pr-1 text-right">
                            <div className="flex justify-end">
                              <NumberInput
                                value={rule.limits.review}
                                onChange={(v) =>
                                  update({ limits: { ...rule.limits, review: v } })
                                }
                              />
                            </div>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}

      {tab === "validation" && (
        <Panel
          title="Validation rules"
          description="Hard checks run on the extracted data. A failing rule marks the check as failed unless it is listed under Continue-on."
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {schema.validation.map((v) => (
              <ToggleRow
                key={v.key}
                label={v.label}
                hint={v.hint}
                on={config.settings.validation[v.key]?.active ?? false}
                onChange={(on) =>
                  setSettings({
                    validation: {
                      ...config.settings.validation,
                      [v.key]: { active: on },
                    },
                  })
                }
              />
            ))}
          </div>
        </Panel>
      )}

      {tab === "decision" && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <ToggleRow
              label="Continue on fail"
              hint="Move to the next product even when this check fails"
              on={config.settings.continueonfail}
              onChange={(v) => setSettings({ continueonfail: v })}
            />
            <ToggleRow
              label="Continue on review"
              hint="Move on while the case waits for an analyst"
              on={config.settings.continueonreview}
              onChange={(v) => setSettings({ continueonreview: v })}
            />
          </div>

          <Panel title="Retries" description="How many times the end-user may resubmit.">
            <div className="flex flex-wrap items-center gap-8">
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold">Retry on fail</span>
                <NumberInput
                  value={config.settings.retryonfail}
                  onChange={(v) => setSettings({ retryonfail: v })}
                  min={0}
                  max={10}
                  suffix="attempts"
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold">Retry on review</span>
                <NumberInput
                  value={config.settings.retryonreview}
                  onChange={(v) => setSettings({ retryonreview: v })}
                  min={0}
                  max={10}
                  suffix="attempts"
                />
              </div>
            </div>
          </Panel>

          <Panel
            title="Continue-on exceptions"
            description="Specific review reasons that should not block the flow."
          >
            <CheckChips
              options={CONTINUE_ON_OPTIONS}
              value={config.settings.continueon.filter((x) =>
                (CONTINUE_ON_OPTIONS as readonly string[]).includes(x),
              )}
              onChange={(v) => setSettings({ continueon: v })}
            />
            {config.settings.continueonfail && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-accent px-3 py-2 text-[11px] text-primary">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                Continue on fail is enabled — the flow completes even if every rule
                above fails. Use with care.
              </div>
            )}
          </Panel>
        </div>
      )}

      {tab === "advanced" && (
        <div className="space-y-5">
          <Panel
            title="Feature flags"
            description="Provider-level behaviour switches stored in the check's flags object."
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {schema.flags.map((f) => (
                <ToggleRow
                  key={f.key}
                  mono
                  label={f.key}
                  hint={f.hint}
                  on={config.flags[f.key] ?? false}
                  onChange={(on) => set({ flags: { ...config.flags, [f.key]: on } })}
                />
              ))}
            </div>
          </Panel>

          {id === "poa" && schema.prompts.map((p) => (
            <Panel
              key={p.key}
              title={p.label}
              description={p.hint}
              right={
                <span className="inline-flex items-center gap-1 rounded bg-surface-100 px-2 py-1 text-[10px] font-semibold text-muted-foreground">
                  <Info className="size-3" />
                  {(config.promptconfig[p.key] ?? "").length} chars
                </span>
              }
            >
              <textarea
                value={config.promptconfig[p.key] ?? ""}
                onChange={(e) =>
                  set({ promptconfig: { ...config.promptconfig, [p.key]: e.target.value } })
                }
                rows={10}
                spellCheck={false}
                placeholder="You are a document classification and data extraction engine…"
                className="w-full resize-y rounded-lg border border-border bg-surface-50 px-3 py-2 font-mono text-[11px] leading-relaxed outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
